import type { GridCell, SizedGridColumn } from "@glideapps/glide-data-grid";
import { getAiSpreadsheetCellDisplayValue } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/cell-display-value";
import type {
  IAiSpreadsheetCell,
  IAiSpreadsheetLoadedSheet,
} from "@/payroll/ai-script/types";
import { isAiSpreadsheetSheetEditable } from "@/payroll/ai-script/utils";
import {
  buildRowHeights,
  DEFAULT_ROW_HEIGHT,
  getColumnTitle,
  getColumnWidth,
} from "./dimensions";
import { buildGridCell, buildNewRowCell } from "./grid-cell";
import { buildTextOverflowSpans } from "./text-overflow-spans";
import {
  type AdaptedSpreadsheetSheet,
  coordinateKey,
  type SpreadsheetAdapterOptions,
} from "./types";

const MIN_COLUMN_COUNT = 8;
const MIN_ROW_COUNT = 20;
const TRAILING_COLUMN_COUNT = 5;
const TRAILING_ROW_COUNT = 5;

export const adaptSpreadsheetSheet = (
  sheet: IAiSpreadsheetLoadedSheet,
  { onHyperlinkClick }: SpreadsheetAdapterOptions = {},
): AdaptedSpreadsheetSheet => {
  const cellsByCoordinate = new Map<string, IAiSpreadsheetCell>();
  let maxCol = 0;
  let maxRow = 0;

  sheet.cells.forEach((cell) => {
    cellsByCoordinate.set(coordinateKey(cell.col, cell.row), cell);
    maxCol = Math.max(maxCol, cell.col);
    maxRow = Math.max(maxRow, cell.row);
  });

  const columnCount = Math.max(
    maxCol + TRAILING_COLUMN_COUNT,
    MIN_COLUMN_COUNT,
  );
  const rowCount = Math.max(maxRow + TRAILING_ROW_COUNT, MIN_ROW_COUNT);

  const columns: SizedGridColumn[] = Array.from(
    { length: columnCount },
    (_, index) => ({
      id: String(index),
      title: getColumnTitle(index),
      width: getColumnWidth(sheet.column_widths, index + 1),
    }),
  );

  const rowHeights = buildRowHeights(
    sheet,
    columns,
    rowCount,
    cellsByCoordinate,
  );
  const textOverflowSpans = buildTextOverflowSpans(
    columnCount,
    cellsByCoordinate,
  );
  const searchableCells = sheet.cells
    .map((cell) => ({
      cell,
      displayValue: getAiSpreadsheetCellDisplayValue(cell).toLocaleLowerCase(),
    }))
    .filter(({ displayValue }) => displayValue !== "")
    .sort((a, b) => a.cell.row - b.cell.row || a.cell.col - b.cell.col);

  const getRecord = (col: number, row: number) =>
    cellsByCoordinate.get(coordinateKey(col + 1, row + 1));
  const getManualRowCell = (row: number) =>
    Array.from(cellsByCoordinate.values()).find(
      (cell) => cell.row === row + 1 && !!cell.manual_row_id,
    );
  const getTextOverflow = (col: number, row: number) =>
    textOverflowSpans.get(coordinateKey(col + 1, row + 1));
  const editable = isAiSpreadsheetSheetEditable(sheet);
  const isAddRowCell = (col: number, row: number) =>
    editable && row === maxRow && col < maxCol;

  const getCellContent = (col: number, row: number): GridCell => {
    const record = getRecord(col, row);
    if (!record && isAddRowCell(col, row)) {
      return buildNewRowCell();
    }
    return buildGridCell(record, editable, onHyperlinkClick);
  };

  return {
    columns,
    rowCount,
    isAddRowCell,
    getCellContent,
    getCellsForSelection: ({ x, y, width, height }) => {
      const rows: GridCell[][] = [];
      for (let row = y; row < y + height; row++) {
        const cols: GridCell[] = [];
        for (let col = x; col < x + width; col++) {
          cols.push(getCellContent(col, row));
        }
        rows.push(cols);
      }
      return rows;
    },
    getCellRecord: (col, row) => getRecord(col, row),
    getManualRowCell,
    getSearchMatches: (query) => {
      const normalizedQuery = query.trim().toLocaleLowerCase();

      if (!normalizedQuery) return [];

      return searchableCells
        .filter(({ displayValue }) => displayValue.includes(normalizedQuery))
        .map(({ cell }) => ({
          cellId: cell.id,
          col: cell.col - 1,
          row: cell.row - 1,
        }));
    },
    getTextOverflow,
    getRowHeight: (row) => rowHeights.get(row) ?? DEFAULT_ROW_HEIGHT,
  };
};
